import { useState } from "react";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { Mail, Phone, MapPin, Send, CheckCircle2, Instagram, Youtube, Linkedin, Twitter, Facebook } from "lucide-react";
import { services } from "@/data/services";

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.55a8.16 8.16 0 0 0 4.77 1.52V6.69z"/>
  </svg>
);

const socialLinks = [
  { Icon: Facebook, href: "https://www.facebook.com/AlmkhrjHanyHjazy?locale=ar_AR", label: "Facebook" },
  { Icon: TikTokIcon, href: "https://www.tiktok.com/@dir.hani.hejazi", label: "TikTok" },
  { Icon: Twitter, href: "https://x.com/HaniHejazi2025", label: "X" },
  { Icon: Instagram, href: "https://www.instagram.com/hani_art_lenss/", label: "Instagram" },
  { Icon: Youtube, href: "https://www.youtube.com/@artlenss2026", label: "YouTube" },
  { Icon: Linkedin, href: "https://www.linkedin.com/in/%D8%A7%D9%84%D9%85%D8%AE%D8%B1%D8%AC-%D9%87%D8%A7%D9%86%D9%8A-%D8%AD%D8%AC%D8%A7%D8%B2%D9%8A-26668378/", label: "LinkedIn" },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", type: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      // Fire-and-forget log to backend (non-blocking for UX)
      axios.post(`${API}/contact`, form).catch(() => {});

      // Compose and open a mailto to the studio inbox with the form data
      const subject = `New brief — ${form.name}${form.company ? " · " + form.company : ""}${form.type ? " · " + form.type : ""}`;
      const bodyLines = [
        `Name: ${form.name}`,
        `Email: ${form.email}`,
        form.phone ? `Phone: ${form.phone}` : null,
        form.company ? `Company: ${form.company}` : null,
        form.type ? `Project type: ${form.type}` : null,
        "",
        "Message:",
        form.message,
      ].filter(Boolean);
      const mailto = `mailto:info@artlenss.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines.join("\n"))}`;
      window.location.href = mailto;

      setStatus("success");
      setForm({ name: "", email: "", phone: "", company: "", type: "", message: "" });
    } catch (err) {
      setStatus("error");
    }
  };

  const socials = socialLinks;

  return (
    <div data-testid="contact-page">
      <section className="pt-24 pb-10 md:pt-28 md:pb-16">
        <div className="container-x max-w-5xl">
          <div className="overline mb-6">{t("contact.eyebrow")}</div>
          <h1 className="display-h1 italic">{t("contact.title")}</h1>
          <p className="mt-8 text-white/70 text-xl leading-relaxed max-w-3xl">{t("contact.lead")}</p>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* FORM */}
          <div className="lg:col-span-7">
            {status === "success" ? (
              <div data-testid="contact-success" className="glass-card rounded-xl p-10 md:p-14 text-center">
                <CheckCircle2 className="w-14 h-14 text-[#E6A95B] mx-auto" />
                <h2 className="display-h3 mt-6">{t("contact.successTitle")}</h2>
                <p className="mt-4 text-white/70 text-lg">{t("contact.successBody")}</p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-8 btn-ghost"
                  data-testid="contact-send-another"
                >
                  {t("contact.submit")}
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="glass-card rounded-xl p-8 md:p-12" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label={t("contact.formName")} name="name" value={form.name} onChange={onChange} required testid="contact-name" />
                  <Field label={t("contact.formEmail")} name="email" type="email" value={form.email} onChange={onChange} required testid="contact-email" />
                  <Field label={t("contact.formPhone")} name="phone" value={form.phone} onChange={onChange} testid="contact-phone" />
                  <Field label={t("contact.formCompany")} name="company" value={form.company} onChange={onChange} testid="contact-company" />
                </div>

                <div className="mt-6">
                  <label className="overline block mb-3">{t("contact.formType")}</label>
                  <select
                    name="type"
                    value={form.type}
                    onChange={onChange}
                    required
                    data-testid="contact-type"
                    className="w-full bg-[#0A0A0C] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#E6A95B] focus:outline-none focus:ring-2 focus:ring-[#E6A95B]/20 transition"
                  >
                    <option value="" disabled>{t("contact.formTypePlaceholder")}</option>
                    {services.map((s) => (
                      <option key={s.id} value={s.id} className="bg-[#0A0A0C]">{s.title[lang]}</option>
                    ))}
                  </select>
                </div>

                <div className="mt-6">
                  <label className="overline block mb-3">{t("contact.formMessage")}</label>
                  <textarea
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={onChange}
                    required
                    data-testid="contact-message"
                    className="w-full bg-[#0A0A0C] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#E6A95B] focus:outline-none focus:ring-2 focus:ring-[#E6A95B]/20 transition resize-none"
                  />
                </div>

                {status === "error" && (
                  <div className="mt-4 text-red-400 text-sm">Something went wrong. Please try again.</div>
                )}

                <div className="mt-8">
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="contact-submit-button"
                    className="btn-primary disabled:opacity-60"
                  >
                    {status === "sending" ? t("contact.sending") : (<><Send className="w-4 h-4" /> {t("contact.submit")}</>)}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* INFO */}
          <div className="lg:col-span-5 space-y-8">
            <div className="glass-card rounded-xl p-8" data-testid="contact-info">
              <div className="overline mb-6">{t("contact.infoTitle")}</div>
              <ul className="space-y-5">
                <li className="flex items-start gap-4">
                  <Mail className="w-5 h-5 text-[#E6A95B] mt-1" />
                  <a href="mailto:info@artlenss.com" className="link-underline text-lg">info@artlenss.com</a>
                </li>
                <li className="flex items-start gap-4">
                  <Phone className="w-5 h-5 text-[#E6A95B] mt-1" />
                  <a href="tel:+966554142007" dir="ltr" className="link-underline text-lg">+966 55 414 2007</a>
                </li>
                <li className="flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-[#E6A95B] mt-1" />
                  <span className="text-lg">{t("contact.location")}</span>
                </li>
              </ul>

              <div className="mt-8 pt-8 border-t border-white/10">
                <div className="overline mb-4">{t("contact.social")}</div>
                <div className="flex gap-3">
                  {socials.map((s, i) => (
                    <a
                      key={i}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.label}
                      data-testid={`contact-social-${s.label.toLowerCase()}`}
                      className="w-11 h-11 grid place-items-center rounded-full border border-white/15 text-white hover:text-[#E6A95B] hover:border-[#E6A95B]/50 transition"
                    >
                      <s.Icon className="w-4 h-4" />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* MAP */}
            <a
              href="https://www.google.com/maps/place/%D8%B9%D8%AF%D8%B3%D8%A9+%D8%A7%D9%84%D9%81%D9%86+%D9%84%D9%84%D8%A7%D9%86%D8%AA%D8%A7%D8%AC+%D8%A7%D9%84%D9%81%D9%86%D9%8A%E2%80%AD/@21.6190025,39.1357725,1048m/data=!3m2!1e3!4b1!4m6!3m5!1s0x15c3d10fe1b31551:0xe02ebbb74f5ff805!8m2!3d21.6189975!4d39.1331976!16s%2Fg%2F11gxf7kgz5"
              target="_blank"
              rel="noopener noreferrer"
              className="relative block rounded-xl overflow-hidden border border-white/10 group"
              data-testid="contact-map"
            >
              <iframe
                title="Art Lens Studio · Jeddah"
                src="https://maps.google.com/maps?q=21.6189975,39.1331976&z=17&hl=ar&output=embed"
                width="100%"
                height="280"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="grayscale contrast-125 opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition"
                style={{ border: 0, pointerEvents: "none" }}
              />
              <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-[#0A0A0C] to-transparent text-white/80 text-xs uppercase tracking-[0.24em]">
                {t("contact.mapCaption")}
              </div>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, name, type = "text", value, onChange, required, testid }) => (
  <div>
    <label className="overline block mb-3">{label}{required && <span className="text-[#E6A95B]"> *</span>}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testid}
      className="w-full bg-[#0A0A0C] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#E6A95B] focus:outline-none focus:ring-2 focus:ring-[#E6A95B]/20 transition"
    />
  </div>
);

export default Contact;
